import { useConfigDrift, useGenerateConfig, useReloadConfig } from "@/hooks/use-config";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";

/**
 * Warns when the generated Asterisk config no longer matches the database.
 *
 * A save that never reaches Asterisk is otherwise silent — the database holds
 * the new value and the save reports success while Asterisk keeps running the
 * old config. That went unnoticed twice: HEP captured against a retired
 * collector for hours, and an extension edit sat unapplied while Asterisk still
 * ran the previous dialplan. Both looked like the UI had worked.
 */
export function ConfigDriftBanner() {
  const { data } = useConfigDrift();
  const generate = useGenerateConfig();
  const reload = useReloadConfig();

  const drifted = data?.drifted_modules ?? [];
  // Two different failures land here. Either the file on disk no longer matches
  // the database, or the file is correct and the AMI reload never landed — the
  // second needs different wording, because "regenerate" is not the remedy and
  // the change is not lost, it is queued behind Asterisk coming back.
  const reloadPending = data?.reload_pending ?? [];
  const unreachable = reloadPending.length > 0 && reloadPending.length === drifted.length;
  if (!data || data.in_sync || drifted.length === 0) return null;

  const busy = generate.isPending || reload.isPending;

  const apply = async () => {
    try {
      for (const module of drifted) {
        await generate.mutateAsync(module);
        await reload.mutateAsync(module);
      }
      toast.success(`Applied: ${drifted.join(", ")}`);
    } catch (err) {
      toast.error(
        `Could not apply config: ${err instanceof Error ? err.message : String(err)}`
      );
    }
  };

  const reasons = (data.modules ?? []).filter((m) => !m.in_sync);

  return (
    <div
      role="status"
      className="mb-4 flex items-start gap-3 rounded-sm border border-warning-border bg-warning-subtle px-4 py-3"
    >
      <AlertTriangle size={16} className="mt-0.5 shrink-0 text-warning-text" />
      <div className="flex-1 space-y-1">
        <p className="text-sm font-medium text-warning-text">
          {unreachable
            ? "Asterisk did not apply your last change"
            : "Asterisk is running an older configuration"}
        </p>
        <p className="text-xs text-muted-foreground">
          {unreachable ? (
            <>
              {reloadPending.join(", ")} {reloadPending.length === 1 ? "was" : "were"}{" "}
              written to disk, but the reload could not reach Asterisk — the change
              is saved and will apply on the next successful reload.
            </>
          ) : (
            <>
              {drifted.join(", ")} {drifted.length === 1 ? "differs" : "differ"} from
              what is saved here, so recent changes are not live yet.
            </>
          )}
        </p>
        {reasons.some((m) => m.reason && !m.reason.startsWith("on-disk")) && (
          <ul className="text-xs text-muted-foreground">
            {reasons
              .filter((m) => m.reason && !m.reason.startsWith("on-disk"))
              .map((m) => (
                <li key={m.module}>
                  {m.module}: {m.reason}
                </li>
              ))}
          </ul>
        )}
      </div>
      <Button size="sm" onClick={apply} loading={busy} disabled={busy}>
        Regenerate &amp; reload
      </Button>
    </div>
  );
}
